import { z } from 'zod'

import type { FirestoreDataConverter } from 'firebase/firestore'
import type { FirestoreDataConverter as AdminFirestoreDataConverter } from 'firebase-admin/firestore'
import type { Timestamp } from 'firebase/firestore'
import type { Timestamp as AdminTimestamp } from 'firebase-admin/firestore'
import { ModelUtils } from './utils'

export class CollectionModelFactory {
  public CollectionDBModel
  public FirestoreCollectionDBModel
  public NewCollectionDBModel
  public collectionConverter: FirestoreDataConverter<
    z.infer<typeof this.CollectionDBModel>,
    z.infer<typeof this.FirestoreCollectionDBModel>
  >
  public adminCollectionConverter: AdminFirestoreDataConverter<
    z.infer<typeof this.CollectionDBModel>,
    z.infer<typeof this.FirestoreCollectionDBModel>
  >
  public getCollectionColor: (color?: string | null) => string

  private utils

  constructor(timestamp: typeof Timestamp | typeof AdminTimestamp) {
    this.utils = new ModelUtils(timestamp)
    const defaultCollectionColor = '#D1D5DB'
    const colorSchema = z
      .preprocess(
        (value) => (typeof value === 'string' ? value.trim() : value),
        z.string().regex(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/),
      )
      .catch(defaultCollectionColor)

    this.CollectionDBModel = z.object({
      id: z.string(),
      owner: z.string(),
      title: z.string(),
      description: z.string(),
      color: colorSchema,
      bundles: z.array(z.string()).default([]),
      created: this.utils.date,
      modified: this.utils.date,
    })

    this.NewCollectionDBModel = this.CollectionDBModel.omit({ id: true })

    this.FirestoreCollectionDBModel = this.CollectionDBModel.omit({
      id: true,
    }).extend({
      created: this.utils.dateForFirestore,
      modified: this.utils.dateForFirestore,
    })

    type CollectionDBModel = z.infer<typeof this.CollectionDBModel>
    type FirestoreCollectionDBModel = z.infer<
      typeof this.FirestoreCollectionDBModel
    >

    this.collectionConverter = {
      toFirestore: (
        collection: CollectionDBModel,
      ): FirestoreCollectionDBModel => {
        return this.FirestoreCollectionDBModel.parse(collection)
      },
      fromFirestore: (snapshot, options) => {
        const data = snapshot.data(options)
        const returnData = this.CollectionDBModel.parse({
          ...data,
          id: snapshot.id,
        })
        return returnData
      },
    }

    this.adminCollectionConverter = {
      toFirestore: (collection: CollectionDBModel) => {
        return this.FirestoreCollectionDBModel.parse(collection)
      },
      fromFirestore: (snapshot) => {
        const data = snapshot.data()
        const returnData = this.CollectionDBModel.parse({
          ...data,
          id: snapshot.id,
        })
        return returnData
      },
    }

    this.getCollectionColor = (color?: string | null) => {
      return colorSchema.parse(color)
    }
  }
}
